/* a11y-p6-1-2026-10-05: one modal manager for the theme's own dialogs (cart drawer, search).
   Loaded from sections/header.liquid before cart-drawer.js. WCAG 2.4.3 and 4.1.2: at most one
   aria-modal dialog is active at a time.

   Rules
   a. Explicit stack in open order. A theme dialog is pushed by opened(id) and removed by closed(id).
      A third party modal (any other visible aria-modal="true" element or open <dialog>, for example the
      Klaviyo signup popup) that appears while a theme dialog is open is pushed on top when it is first
      seen. Nothing is inferred from a snapshot of what was already on the page.
   b. prepare(id, done): before a theme dialog opens, other theme dialogs are closed (they are mutually
      exclusive) and any visible third party modal is dismissed through its own close control
      ([aria-label="Close dialog"], then other close buttons), with an Escape key event as the fallback.
      done() runs once none is visible, in the same task as that check. If one cannot be dismissed in
      1.5 s, done() runs anyway and that modal is pushed above ours, so ours opens suspended (rule c).
   c. A theme dialog with anything above it is suspended: inert first, then aria-hidden="true", trap
      paused through onSuspend(). Previous inert and aria-hidden values are remembered. Focus that was
      inside it moves into the upper modal.
   d. A third party entry leaves the stack as soon as it is no longer visible, by any path: a body subtree
      MutationObserver (childList plus inert, aria-hidden, class, style, hidden, open, aria-modal), transitionend
      and animationend, focusin, and a 250 ms visibility poll while anything is suspended. On restore only
      what we added is removed and focus goes to the dialog's close button. */
(function () {
  if (window.CwModalStack) return;
  var hasInert = 'inert' in HTMLElement.prototype;
  var raf = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : function (fn) { return setTimeout(fn, 16); };
  var reg = {};            // id -> config {root, isOpen, close, focusClose, focusInside, onSuspend, onResume}
  var stack = [];          // bottom first: {id, kind: 'theme' | 'third', el, seen, suspended, saved}
  var mo = null, poll = null, pending = false, trail = null, preparing = {};

  function cfgOf(e) { return e.kind === 'theme' ? reg[e.id] : null; }
  function inTheme(el) {
    for (var id in reg) {
      var r = reg[id].root;
      if (r && (r === el || r.contains(el) || el.contains(r))) return true;
    }
    return false;
  }
  /* rendered and on screen; marks set by others (hidden, inert, aria-hidden on an ancestor) count as closed */
  function shown(el) {
    if (!el || !el.isConnected) return false;
    if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    if (el.checkVisibility && !el.checkVisibility({ visibilityProperty: true, opacityProperty: true })) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 0 && r.bottom > 0 && r.left < window.innerWidth && r.top < window.innerHeight;
  }
  function thirdParty() {
    return [].slice.call(document.querySelectorAll('[aria-modal="true"], dialog[open]'))
      .filter(function (m) { return !inTheme(m) && shown(m); })
      .filter(function (m, i, all) { return !all.some(function (o) { return o !== m && o.contains(m); }); });
  }
  function onStack(el) { return stack.some(function (e) { return e.el === el; }); }
  function themeEntry(id) { for (var i = 0; i < stack.length; i++) if (stack[i].kind === 'theme' && stack[i].id === id) return stack[i]; return null; }
  function tabbables(scope) {
    return [].slice.call(scope.querySelectorAll('button:not([disabled]), a[href], input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'))
      .filter(function (el) { return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'; });
  }
  function closeControl(m) {
    return m.querySelector('[aria-label="Close dialog"]') || m.querySelector('.klaviyo-close-form') ||
      m.querySelector('button[aria-label*="close" i], [role="button"][aria-label*="close" i]');
  }
  function escapeOn(m) {
    var init = { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true };
    try {
      var t = (document.activeElement && m.contains(document.activeElement)) ? document.activeElement : m;
      t.dispatchEvent(new KeyboardEvent('keydown', init));
      t.dispatchEvent(new KeyboardEvent('keyup', init));
    } catch (e) {}
  }

  /* ---------- suspend / restore (rules c, d) */
  function suspend(e, upper) {
    var c = cfgOf(e); if (!c || e.suspended) return;
    var root = c.root;
    e.suspended = true;
    e.saved = { inert: root.hasAttribute('inert'), ah: root.getAttribute('aria-hidden') };
    if (c.onSuspend) c.onSuspend();
    var a = document.activeElement;
    if (upper && upper.el && a && root.contains(a)) {
      var f = tabbables(upper.el)[0];
      if (f) { try { f.focus({ preventScroll: true }); } catch (x) {} }
    }
    if (hasInert) root.inert = true;
    else tabbables(root).forEach(function (el) { el.setAttribute('data-cw-tabindex', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : ''); el.setAttribute('tabindex', '-1'); });
    root.setAttribute('aria-hidden', 'true');
  }
  function restore(e, refocus) {
    var c = cfgOf(e); if (!c || !e.suspended) return;
    var root = c.root, s = e.saved || { inert: false, ah: null };
    e.suspended = false;
    if (s.ah === null) root.removeAttribute('aria-hidden'); else root.setAttribute('aria-hidden', s.ah);
    if (hasInert) { if (!s.inert) root.inert = false; }
    [].slice.call(root.querySelectorAll('[data-cw-tabindex]')).forEach(function (el) {
      var v = el.getAttribute('data-cw-tabindex');
      if (v === '') el.removeAttribute('tabindex'); else el.setAttribute('tabindex', v);
      el.removeAttribute('data-cw-tabindex');
    });
    if (c.onResume) c.onResume();
    if (refocus) { focusTop(c, 'close'); guard(e, 'close'); }
  }
  function focusTop(c, how) {
    try { if (how === 'close' && c.focusClose) c.focusClose(); else if (c.focusInside) c.focusInside(); } catch (x) {}
  }
  /* third party scripts sometimes hand focus back to their own trigger a moment after closing; while our
     dialog is the active top layer, focus that ends up outside it (and outside any visible modal) is returned */
  function guard(e, how) {
    [150, 400, 800].forEach(function (ms) {
      setTimeout(function () {
        if (stack[stack.length - 1] !== e || e.suspended) return;
        var c = cfgOf(e); if (!c || !c.isOpen()) return;
        var a = document.activeElement;
        if (a && a !== document.body && c.root.contains(a)) return;
        var m = a && a.closest ? a.closest('[aria-modal="true"], dialog[open]') : null;
        if (m && !inTheme(m) && shown(m)) { schedule(); return; }
        focusTop(c, how);
      }, ms);
    });
  }

  /* ---------- evaluation */
  function evaluate() {
    stack = stack.filter(function (e) {
      if (e.kind === 'third') return shown(e.el);
      var c = cfgOf(e); if (!c) return false;
      if (c.isOpen()) { e.seen = true; return true; }
      if (e.seen) { restore(e, false); return false; }   // closed by other code without closed(id)
      return true;                                         // opened(id) ran, the open class is still pending
    });
    if (stack.some(function (e) { return e.kind === 'theme'; })) {
      thirdParty().forEach(function (m) { if (!onStack(m)) stack.push({ kind: 'third', el: m }); });
    }
    var top = stack[stack.length - 1], anySuspended = false;
    stack.forEach(function (e, i) {
      if (e.kind !== 'theme') return;
      if (i < stack.length - 1) { suspend(e, top); anySuspended = true; }
      else if (e.suspended) restore(e, true);
    });
    if (anySuspended && !poll) poll = setInterval(evaluate, 250);
    if (!anySuspended && poll) { clearInterval(poll); poll = null; }
    if (!stack.length) stopWatch();
  }
  function schedule() {
    if (pending) return;
    pending = true;
    raf(function () {
      pending = false; evaluate();
      clearTimeout(trail); trail = setTimeout(evaluate, 300);
    });
  }
  function onFocusIn() { if (stack.length) schedule(); }
  function startWatch() {
    if (mo || !window.MutationObserver) return;
    mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['aria-modal', 'class', 'style', 'hidden', 'open', 'aria-hidden', 'inert'] });
    ['transitionend', 'transitioncancel', 'animationend', 'animationcancel'].forEach(function (t) { document.addEventListener(t, schedule, true); });
    document.addEventListener('focusin', onFocusIn, true);
  }
  function stopWatch() {
    if (mo) { mo.disconnect(); mo = null; }
    ['transitionend', 'transitioncancel', 'animationend', 'animationcancel'].forEach(function (t) { document.removeEventListener(t, schedule, true); });
    document.removeEventListener('focusin', onFocusIn, true);
    if (poll) { clearInterval(poll); poll = null; }
    clearTimeout(trail);
  }

  /* ---------- public API */
  window.CwModalStack = {
    register: function (id, cfg) { reg[id] = cfg; },
    unregister: function (id, root) {
      if (!reg[id] || (root && reg[id].root !== root)) return;
      var e = themeEntry(id); if (e) { restore(e, false); stack.splice(stack.indexOf(e), 1); }
      delete reg[id];
      if (!stack.length) stopWatch();
    },
    isSuspended: function (id) { var e = themeEntry(id); return !!(e && e.suspended); },
    /* rule b */
    prepare: function (id, done) {
      if (!reg[id]) { done(); return; }
      if (preparing[id]) { preparing[id].done = done; return; }
      for (var o in reg) {
        if (o !== id && reg[o].isOpen && (reg[o].isOpen() || themeEntry(o))) { try { reg[o].close(); } catch (x) {} }
      }
      var job = preparing[id] = { done: done, tried: [], esc: [], t0: Date.now() };
      function finish(blocked) {
        delete preparing[id];
        job.done();
        if (blocked && blocked.length) schedule();
      }
      function step() {
        if (preparing[id] !== job) return;
        var tp = thirdParty();
        if (!tp.length) { finish(); return; }
        var age = Date.now() - job.t0;
        if (age > 1500) { finish(tp); return; }
        tp.forEach(function (m) {
          if (job.tried.indexOf(m) < 0) {
            job.tried.push(m);
            var c = closeControl(m);
            if (c) { try { c.click(); } catch (x) { escapeOn(m); } } else { escapeOn(m); job.esc.push(m); }
          } else if (age > 600 && job.esc.indexOf(m) < 0) { job.esc.push(m); escapeOn(m); }
        });
        setTimeout(step, 50);
      }
      step();
    },
    opened: function (id) {
      if (!reg[id] || themeEntry(id)) return;
      var e = { kind: 'theme', id: id, seen: false };
      stack.push(e);
      startWatch();
      guard(e, 'inside');
      schedule();
    },
    closed: function (id) {
      var e = themeEntry(id); if (!e) return;
      restore(e, false);
      stack = stack.slice(0, stack.indexOf(e));   // third party entries above it are not ours to track any more
      if (!stack.length) stopWatch(); else schedule();
    }
  };
})();
